import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ImageOff, MapPin, ScanLine } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/sku-check.css";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { playSuccess, playError } from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import FitWord from "../components/FitWord";
import ScanStatusIcons from "../components/ScanStatusIcons";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import { apiFetch } from "../api/apiFetch";

type Phase = "awaiting-scan" | "details";

interface Product {
    sku: string;
    barcode: string | null;
    title: string | null;
    supplier: string | null;
    imageUrl: string | null;
    weightGrams: number | null;
}

interface LocationRow {
    location: string;
    locationName: string;
    qty: number;
}

interface LookupResult {
    product: Product;
    totals: { sellable: number; unsellable: number };
    locations: { sellable: LocationRow[]; unsellable: LocationRow[] };
}

const SkuCheck = () => {
    usePageTitle("sku_check");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };

    const [phase, setPhase] = useState<Phase>("awaiting-scan");
    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [result, setResult] = useState<LookupResult | null>(null);

    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const mountedRef = useRef(true);
    const phaseRef = useRef(phase);
    useEffect(() => { phaseRef.current = phase; }, [phase]);
    useEffect(() => () => {
        mountedRef.current = false;
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    }, []);

    const identity = () => ({
        uid: userObject.uid,
        name: userObject.name,
        warehouse: userObject.warehouse,
    });

    const post = async (path: string, body: Record<string, unknown>) => {
        const res = await apiFetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...body, ...identity() }),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return null;
        }
        return res;
    };

    // Flash the error icon briefly on the scan screen (no command words while active).
    const flashError = () => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus("error");
        statusTimerRef.current = setTimeout(() => {
            if (mountedRef.current) setScanStatus("idle");
        }, 800);
    };

    const notFound = () => {
        playError();
        if (phaseRef.current === "awaiting-scan") flashError();
        else toast.error(t("sku_check_not_found"), { toastId: "sku-not-found" });
    };

    const handleLookup = async (val: string) => {
        const res = await post(`/sku-check/lookup`, { barcode: val });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "sku-server" });
            return;
        }
        if (!data.found || !data.product) {
            notFound();
            return;
        }
        playSuccess();
        setResult({
            product: data.product,
            totals: data.totals ?? { sellable: 0, unsellable: 0 },
            locations: {
                sellable: data.locations?.sellable ?? [],
                unsellable: data.locations?.unsellable ?? [],
            },
        });
        setScanStatus("idle");
        setPhase("details");
    };

    const doSubmit = async (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        if (!val) return;
        setLoading(true);
        try {
            await handleLookup(val);
        } catch {
            if (mountedRef.current) {
                playError();
                toast.error(t("server_error"), { toastId: "sku-server" });
            }
        } finally {
            if (mountedRef.current) { setLoading(false); setScanValue(""); }
        }
    };

    const handleBack = () => navigate("/stock-control");

    const commandWords = t("sku_check_scan_prompt").toUpperCase().split(/[\s-]+/).filter(Boolean);

    const renderLocationSection = (label: string, rows: LocationRow[]) => (
        <div className="sku-check-loc-section">
            <div className="sku-check-loc-head">{label}</div>
            {rows.length === 0
                ? <div className="sku-check-loc-empty">{t("sku_check_no_stock")}</div>
                : rows.map(r => (
                    <div key={r.location} className="sku-check-loc-row">
                        <span className="sku-check-loc-name">
                            <MapPin size={15} />
                            {r.locationName}
                        </span>
                        <span className="sku-check-loc-qty">{r.qty}</span>
                    </div>
                ))}
        </div>
    );

    return (
        <div
            className="scan-panel sku-check-panel"
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
            <div className="page-title-bar">
                <h1 className="page-title">{t("sku_check")}</h1>
                <button className="page-back-btn" onClick={handleBack}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {phase === "awaiting-scan" && (
                <div className="scan-centre">
                    <div className="scan-command-wrap">
                        <ScanStatusIcons status={scanStatus === "error" ? "error" : "idle"} />
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {commandWords.map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {phase === "details" && result && (
                <div className="sku-check-body">
                    <div className="sku-check-card">
                        <div className="sku-check-image">
                            {result.product.imageUrl
                                ? <img src={result.product.imageUrl} alt={result.product.title ?? result.product.sku} />
                                : (
                                    <div className="sku-check-image-placeholder">
                                        <ImageOff size={32} />
                                        <span>{t("sku_check_no_image")}</span>
                                    </div>
                                )}
                        </div>
                        <h2 className="sku-check-title">{result.product.title ?? result.product.sku}</h2>
                        <div className="sku-check-fields">
                            <div className="sku-check-field">
                                <span className="sku-check-field-label">{t("sku_check_sku")}</span>
                                <span className="sku-check-field-value">{result.product.sku}</span>
                            </div>
                            <div className="sku-check-field">
                                <span className="sku-check-field-label">{t("sku_check_barcode")}</span>
                                <span className="sku-check-field-value">{result.product.barcode ?? "—"}</span>
                            </div>
                            <div className="sku-check-field">
                                <span className="sku-check-field-label">{t("sku_check_supplier")}</span>
                                <span className="sku-check-field-value">{result.product.supplier ?? "—"}</span>
                            </div>
                            <div className="sku-check-field">
                                <span className="sku-check-field-label">{t("sku_check_weight")}</span>
                                <span className="sku-check-field-value">
                                    {result.product.weightGrams != null ? `${result.product.weightGrams} g` : "—"}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="sku-check-totals">
                        {t("sku_check_totals", {
                            sellable: String(result.totals.sellable),
                            unsellable: String(result.totals.unsellable),
                        })}
                    </div>

                    <div className="sku-check-locations">
                        {renderLocationSection(t("sku_check_sellable"), result.locations.sellable)}
                        {renderLocationSection(t("sku_check_unsellable"), result.locations.unsellable)}
                    </div>

                    <div className="sku-check-rescan-hint">
                        <ScanLine size={16} />
                        {t("sku_check_scan_another")}
                    </div>
                </div>
            )}

            {/* Scanner stays live in both phases: scanning a new barcode while
                viewing a product swaps the details in place (requirement #4).
                Only blocked during an in-flight lookup so a half-typed barcode
                doesn't race the previous request. */}
            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={loading}
                allowReadOnlyScans
            />
        </div>
    );
};

export default SkuCheck;
